"use client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { OverlapSchemes } from "@/type/overlapType";
import { transformDataPerformanceData } from "@/utils/comparePageFunctions";
import { PerformaceCategoryTabs, RawPerformanceRow } from "@/type/comparePageTypes";

export default function PerformanceLineChart({ funds }: { funds: OverlapSchemes[] }) {
  const [category, setCategory] = useState<PerformaceCategoryTabs>("monthly");
  const [rawMap, setRawMap] = useState<Record<string, RawPerformanceRow[]>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);

      const results = await Promise.all(
        funds.map(async (f) => {
          const res = await fetch(`/api/performanceReturns?slug=${f.url}&id=${f.id}`);

          const data = (await res.json()) as RawPerformanceRow[];

          return { label: f.scheme_name, data };
        }),
      );

      const map: Record<string, RawPerformanceRow[]> = {};

      results.forEach((r) => {
        map[r.label] = r.data;
      });

      setRawMap(map);
      setLoading(false);
    }

    fetchAll();
  }, [funds]);

  const { categories, series } = useMemo(() => {
    const allLabels: string[] = [];

    const transformed = Object.entries(rawMap).map(([label, raw]) => {
      const values = transformDataPerformanceData(raw, category);
      
      values.forEach((v) => {
        if (!allLabels.includes(v.year)) allLabels.push(v.year);
      });
      
      return { label, values };
    });
 

    const chartSeries = transformed.map((t) => ({
      name: t.label,
      type: "line" as const,
      data: allLabels.map((l) => {
        const found = t.values.find((v) => v.year === l);
        return found?.value ?? 0;
      }),
    }));

    return {
      categories: allLabels,
      series: chartSeries,
    };
  }, [rawMap, category]);

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 360,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories,
    },
    yAxis: {
      title: {
        text: undefined,
      },
    },
    tooltip: {
      shared: true,
      valueSuffix: "%",
    },
    plotOptions: {
      line: {
        marker: {
          enabled: false,
        },
      },
    },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "top",
    },
    series: series.map((s) => ({
      ...s,
      type: "column",
    })),
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 728,
          },
          chartOptions: {
            chart: { type: "line", height: 400 },
            series: series.map((s) => ({
              ...s,
              type: "line",
            })),
            legend: {
              itemWidth: 220,
              itemStyle: {
                fontSize: "12px",
              },
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: "11px",
                },
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 431,
          },
          chartOptions: {
            legend: {
              layout: "vertical",
              align: "center",
              itemWidth: undefined,
              itemStyle: {
                fontSize: "11px",
              },
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: "10px",
                },
              },
            },
            plotOptions: {
              series: {
                lineWidth: 2,
              },
            },
          },
        },
      ],
    },
  };

  return (
    <div className="w-full rounded-3xl overflow-hidden border border-gray-200 bg-white">
      {loading ? (
        <div className="flex items-center justify-center h-400 sm:h-90">
          <Image src="/next_assets/images/gif/chart_loading.gif" unoptimized alt="Loading Chart Animation" loading="eager" width={256} height={144} priority className="w-64 h-36 mx-auto" />
        </div>
      ) : (
        <div className="h-400 sm:h-90 p-1">
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>
      )}

      <div className="flex items-center justify-center gap-3 mt-5 p-4 sm:p-5 pt-0 sm:pt-0">
        {(["monthly", "quarterly", "yearly"] as PerformaceCategoryTabs[]).map((c) => (
          <button key={c} onClick={() => setCategory(c)} className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-medium text-2xs sm:text-2sm transition border capitalize ${category === c ? "bg-primaryBlue text-white border-primaryBlue" : "border-primaryBlue/30 text-primaryBlack bg-gray-100 hover:bg-gray-200"}`}>
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}
